import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { api } from "@/api/client";

/** Compact data-health status. Details stay available without occupying a full row. */
export default function DataAndonBanner() {
  const [dismissedAt, setDismissedAt] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ["data-andon"],
    queryFn: api.dataAndon,
    staleTime: 60_000,
    refetchInterval: 3 * 60_000,
    refetchOnWindowFocus: true,
  });

  const data = q.data;
  if (!data || data.level === "green") return null;

  const red = data.level === "red";
  const dismissKey = `${data.level}:${data.reference_date}`;
  if (!red && dismissedAt === dismissKey) return null;

  const critical = data.stale.filter((item) => item.group === "critical");
  const derived = data.stale.filter((item) => item.group === "derived");
  const derivedMessage = derived.length
    ? `核心行情正常；研究数据待处理：${derived.map((item) => `${item.name} 最新 ${item.last ?? "暂无"}，相差 ${item.lag_days} 个交易日`).join("；")}。`
    : data.message;

  return <details className="group relative z-40 text-xs">
    <summary className={`flex cursor-pointer list-none items-center gap-1.5 rounded-md border px-2.5 py-1.5 font-medium ${red ? "border-red-300 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950" : "border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950"}`}>
      <span>{red ? "🚨" : "⚠️"}</span><span className="hidden sm:inline">数据健康</span><span>{red ? "异常" : "需处理"}</span>
    </summary>
    <div className="absolute right-0 top-[calc(100%+8px)] z-50 w-[min(32rem,calc(100vw-1.5rem))] rounded-lg border bg-white p-4 text-zinc-700 shadow-xl dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200" role="alert">
      <div className="flex items-start justify-between gap-3">
        <div><div className="font-semibold">{red ? "数据停更告警" : "数据更新提醒"}</div><p className="mt-1 leading-5 text-zinc-600 dark:text-zinc-300">{red ? "核心行情数据已停止更新，相关研究结果可能基于陈旧数据。" : derivedMessage}</p></div>
        {!red && <button type="button" onClick={() => setDismissedAt(dismissKey)} className="shrink-0 text-lg text-zinc-400" aria-label="今日关闭提醒">×</button>}
      </div>
      {critical.length > 0 && <div className="mt-2 font-mono text-[11px]">{critical.map((item) => `${item.name} 停在 ${item.last ?? "—"}（相差 ${item.lag_days} 个交易日）`).join("；")}</div>}
      {red && derived.length > 0 && <div className="mt-1 text-[11px] text-zinc-500">另有 {derived.map((item) => item.name).join("、")} 滞后</div>}
      <Link to="/" className="mt-3 inline-block font-medium text-blue-600">查看数据健康 →</Link>
    </div>
  </details>;
}
